import React from 'react';
import ReactDOM from "react-dom";

import {
  ColorLibrary,
  colorFromSteps,
  colorMapsFromMinMaxFactory,
  constantStepCalculator
} from '../utils/color-maps';

import {
  makeLegend
} from 'utils/utils';

import {
  Epochs,
  HazardTypes,
  ClimateFutures
} from './constants';

import backgroundConfig from 'config/map-layers/background';
import naturalCapitalConfig from 'config/map-layers/natural-capital';
import physicalGeographyConfig from 'config/map-layers/pysical-geography';
import socialGeographyConfig from 'config/map-layers/social-geography';
import floodHazardConfig from 'config/map-layers/flood-hazard';
import floodRiskConfig, { floodHazards } from 'config/map-layers/flood-risks';
import floodFlowConfig from 'config/map-layers/flood-flows';


import { smallBlueIcon, smallGreenIcon } from "../components/map/icons";

const S3_BASE_URL = 'https://tiff-library.s3.eu-west-2.amazonaws.com/ethiopia';


const defaultReliefCosts = {
  attributionString: 'Vivid economics',
  onEachFeature: (feature, layer) => {
    layer.bindPopup(`Priority: ${feature.properties.priority} <br/>Costs: ${parseFloat(feature.properties.costs).toFixed(2)}  (lakh taka)`)
  },
  pointToLayer: function (feature, latlng) {

    const toColor = priority => {
      switch (priority) {
        case 1: return "#bf0d0d"
        case 2: return "#f19203"
        case 3: return "#e3ea08"
      }
    }

    const geojsonMarkerOptions = {
      radius: Math.round(feature.properties.costs/4),
      fillColor: toColor(feature.properties.priority),
      color: "#000",
      weight: 1,
      opacity: 1,
      fillOpacity: 0.8
    };
    return window.L.circleMarker(latlng, geojsonMarkerOptions);
  }
}

const floodEventMapDefaults = {
  zIndex: 410,
  transparentValues: [0],
  opacity: 1,
  pixelValuesToColorFn: (([value]) => {
    if (value) return '#007af8';
    return `rgba(0,0,0,0)`;
  })
}

const nowCastFloodHazardDefaults = {
  title: 'Flood Hazard',
  pixelValuesToColorFn: (([value]) => {
    if (value === 0) return ColorLibrary.transparent;
    if (value === 1) return '#007af8';
  }),
  zIndex: 410,
  legend: [
    {text: 'Observed water', color: '#007af8'}
  ]
}

const floodDepthForecastDefaults = {
  zIndex: 410,
  transparentValues: [128],
  opacity: 1,
  pixelValuesToColorFn: (([value]) => {
    if (value < 0.2) return `rgba(0,0,0,0)`;
    if (value < 5) return '#bdd7e7';
    if (value < 10) return '#6baed6';
    return '#2171b5';
  }),
  title: 'Depth (m)',
  legend: makeLegend([0, 5, 10, 40],['#bdd7e7','#6baed6','#2171b5'])
}

const floodDepthEventHazardForecastDefaults = {
  ...floodDepthForecastDefaults,
  pixelValuesToColorFn: colorMapsFromMinMaxFactory({
    colorSteps: ['#eff3ff','#bdd7e7','#6baed6', '#3182bd', '#08519c'],
    min: 0.1,
    max: 10,
    transparentValues: [0]
  }),
  legend: [
    {text: '0.1', color: '#eff3ff'},
    {text: '2.5', color: '#bdd7e7'},
    {text: '5', color: '#6baed6'},
    {text: '7.5', color: '#3182bd'},
    {text: '10', color: '#08519c'},
  ]
}

const floodSpeedEventHazardForecastDefaults = {
  ...floodDepthForecastDefaults,
  title: 'Speed (m/s)',
  pixelValuesToColorFn: colorMapsFromMinMaxFactory({
    colorSteps: ['#fee0d2','#fc9272','#de2d26'],
    min: 0.1,
    max: 3,
    transparentValues: [0]
  }),
  legend: [
    {text: '0.1', color: '#fee0d2'},
    {text: '1.5', color: '#fc9272'},
    {text: '3', color: '#de2d26'},
  ]
}

const floodDangerEventHazardForecastDefaults = {
  ...floodDepthForecastDefaults,
  title: 'Danger',
  pixelValuesToColorFn: (([value]) => {
    if (value <= 0) return ColorLibrary.transparent;
    if (value <= 1.5) return '#fee0d2';
    if (value <= 2.5) return '#fc9272';
    return '#de2d26'
  }),
  legend: [
    {text: 'Danger for some', color: '#fee0d2'},
    {text: 'Danger for most', color: '#fc9272'},
    {text: 'Danger for all', color: '#de2d26'},
  ]
}

const chirpsDefaults = {
  zIndex: 410,
  opacity: 1,
  pixelValuesToColorFn: colorMapsFromMinMaxFactory({
    colorSteps: ['#fff7fb','#ece7f2','#d0d1e6','#a6bddb','#74a9cf','#3690c0','#0570b0','#045a8d','#023858'],
    min: 0,
    max: 140,
    transparentValues: [0]
  })
};

const HazardOptions = Object.keys(HazardTypes).map(hazardType => {
  return {
    text: HazardTypes[hazardType],
    key: HazardTypes[hazardType],
    value: HazardTypes[hazardType]
  };
});





const LayerConfig = {
  topography: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/background/cogeo-topo.tif',
    attributionString: "ASTER",
    pixelValuesToColorFn: colorMapsFromMinMaxFactory({
      colorSteps: [
        '#336600',
        '#72B219',
        '#CAE683',
        '#FEF4B5',
        "#f9db82",
        "#f5c24e",
        "#9c6721",
        "#663307",
        "#714112",
        "#ab8f73",
        "#e6ddd5"],
      min: -50,
      max: 0,
      transparentValues: [-32768]
    }),
    resolution: 128,
    opacity: 0.7,
    zIndex: 400,
    // TODO: Fix this horrible mess!
    title: 'Elevation (m)',
    legend: makeLegend(
      [-50, ...constantStepCalculator(11, -50, 0)],
      ['#336600', '#72B219','#CAE683', '#FEF4B5',"#f9db82","#f5c24e","#9c6721","#663307","#714112","#ab8f73","#e6ddd5"]
    )
  },
  landUse: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/social_geography/LULC_2005_bihar.cogeo.tif',
    name: 'landUse',
    attributionString: 'Decadal LULC, 2005',
    zIndex: 400,
    opacity: 0.8,
    pixelValuesToColorFn: (([value]) => {
      if (value === 1) return '#00a900';
      if (value === 2) return '#ffd27f';
      if (value === 3) return '#ff0000';
      if (value === 4) return '#66e600';
      if (value === 5) return '#c43500';
      if (value === 6) return '#dec39d';
      if (value === 7) return '#feff68';
      if (value === 8) return '#860000';
      if (value === 9) return '#00e0ff';
      if (value === 10) return '#00ff00';
      if (value === 14) return '#e28a64';
      if (value === 17) return '#00e0ff';
    }),
    title: 'Land Use',
    legend: [
      {color: '#00a900', text: 'Deciduous Broadleaf Forest'},
      {color: '#ffd27f', text: 'Cropland'},
      {color: '#ff0000', text: 'Built-up Land'},
      {color: '#66e600', text: 'Mixed Forest'},
      {color: '#c43500', text: 'Shrubland'},
      {color: '#dec39d', text: 'Barren Land'},
      {color: '#feff68', text: 'Fallow Land'},
      {color: '#860000', text: 'Wasteland'},
      {color: '#00e0ff', text: 'Water Bodies'},
      {color: '#00ff00', text: 'Plantations'},
      {color: '#e28a64', text: 'Grassland'},
      {color: '#00e0ff', text: 'Permanent Wetlands'},
    ]

  },
  nfvi: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/kent/social_geography/nfvi.zip',
    name: 'nfvi',
    text: 'NFVI',
    style: (feature) => {
      const get_color = (nfvi) => {
        if (nfvi <= -2.5) return '#FEF0D9';
        if (nfvi <= -1.5) return '#FED8A4';
        if (nfvi <= -.5) return '#FDB77A';
        if (nfvi <= .5) return '#FC8D59';
        if (nfvi <= 1.5) return '#EC603F';
        if (nfvi <= 2.5) return '#D33122';
        return '#B30000'
      }
      return {
        color: get_color(feature.properties['nfvi']),
        fillOpacity: 0.9
      }
    },
    legend: [
      {color: '#FEF0D9', text: 'Slight'},
      {color: '#FED8A4', text: 'Extremely low'},
      {color: '#FDB77A', text: 'Relatively low'},
      {color: '#FC8D59', text: 'Average'},
      {color: '#EC603F', text: 'Relatively high'},
      {color: '#D33122', text: 'Extremely high'},
      {color: '#B30000', text: 'Acute'},
    ]
  },
  sfri: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/kent/social_geography/sfri.zip',
    name: 'sfri',
    text: 'SFRI',
    style: (feature) => {
      const get_color = (sfri) => {
        if (sfri <= -5) return '#ffffb2';
        if (sfri <= -1) return '#ffcc66';
        if (sfri <= 1) return '#fd8d3c';
        if (sfri <= 5) return '#f03b20';
        return '#bd0026'
      }
      return {
        color: get_color(feature.properties['sfri_pd']),
        fillOpacity: 0.9
      }
    },
    legend: [
      {color: '#ffffb2', text: '<= -5'},
      {color: '#ffcc66', text: '-5 - -1'},
      {color: '#fd8d3c', text: '-1 - 1'},
      {color: '#f03b20', text: '1 - 5'},
      {color: '#bd0026', text: '> 5'},
    ]
  },

  osmWaterNetwork: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/physical_geography/osm_water_bihar.zip',
    name: 'osm_water',
    title: 'OSM Rivers and Water',
    attributionString: 'Open Street Map',
    style: () => {
      return {
        stroke: true,
        weight: 1,
        color: '#007af8',
        fillOpacity: 1
      }
    },
    legend: [
      {color: '#007af8', text: 'River'}
    ]
  },
  plannedDamsConfig: {
      url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/ethiopia/shape-files/planned_dams.zip',
      name: 'awash-planned-dams',
      attributionString: '',
      opacity: 0.9,
      zIndex: 420
  },
  flowAccumulationsConfig: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/physical_geography/flow_accumulations_bihar.cogeo.tif',
    name: 'flow-accumulations',
    title: 'River network and catchment areas (30s)',
    attributionString: 'HydroSHEDS',
    zIndex: 420,
    opacity: 0.7,
    pixelValuesToColorFn: (value) => {
      if (value < 10) {
        return ColorLibrary.transparent
      }
      const steps = [10,150, 300,450,600,99999999];
      const colors = ['#dadaeb','#bcbddc','#9e9ac8','#756bb1','#54278f'];
      return colorFromSteps(parseInt(value), steps, colors, 'green');
    },
    legend: [
      { color: '#dadaeb', text: '10 - 150' },
      { color: '#bcbddc', text: '150 - 300' },
      { color: '#9e9ac8', text: '300 - 450' },
      { color: '#756bb1', text: '450 - 600' },
      { color: '#54278f', text: ' > 600' },
    ]
  },

  riverBasins: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/physical_geography/hydrosheds_river_basins_15s_bihar.zip',
    name: 'river-basins',
    title: 'Hydrosheds River Basins',
    attributionString: 'HydroSHEDS',
  },



  gauges: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/physical_geography/Bihar_Stations.zip',
    name: 'gauges',
    title: 'Gauges',
    attributionString: '',
    onEachFeature: ((feature, layer, setMapOptions) => {
      layer.setIcon(smallBlueIcon)

      layer.on('click', ({...args}) => {
        setMapOptions({gaugeData: feature.properties['Site_Name']});
      } )
    }),
  },

  populationCensus2011: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/social_geography/india_pop_census2011.cogeo.tif',
    name: 'popconsus2011',
    text: 'Population',
    attributionString: 'India Census 2011',
    zIndex: 420,
    pixelValuesToColorFn: (([value]) => {
      if (value <= 0) return `rgba(0,0,0,0)`
      if (value <= 1000) return '#cccccc';
      if (value <= 5000) return '#969696';
      if (value <= 15000) return '#636363';
      return '#252525';
    }),
    title: 'Population (1kmx1km)',
    legend: makeLegend(['0', '1,000', '5,000', '15,000'], ['#cccccc', '#969696', '#636363', '#252525'])
  },



  settlementTypesGHS: {
    url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/social_geography/ghs_settlement_types.bihar.cogeo.tif',
    name: 'ghs_settlement_types',
    text: 'Settlement Types',
    attributionString: 'JRC World Data',
    zIndex: 420,
    pixelValuesToColorFn: (([value]) => {
      if (value === 10) return '#7AB6F5';
      if (value === 11) return '#CDF57A';
      if (value === 12) return '#ABCD66';
      if (value === 13) return '#375623';
      if (value === 21) return '#FFFF00';
      if (value === 22) return '#A87000';
      if (value === 23) return '#732600';
      if (value === 30) return '#FF0000';
    }),
    title: '',
    legend: [
      {color: '#7AB6F5', text: 'Water'},
      {color: '#CDF57A', text: 'Very low density rural'},
      {color: '#ABCD66', text: 'Low density rural'},
      {color: '#375623', text: 'Rural cluster'},
      {color: '#FFFF00', text: 'Suburban or per-urban'},
      {color: '#A87000', text: 'Semi-dense urban cluster'},
      {color: '#732600', text: 'Dense urban cluster'},
      {color: '#FF0000', text: 'Urban center'},

    ]
  },

    people_in_poverty_exposed_to_flooding: {
      url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/nowcast/2020-05-17/village_population_exposed_to_10_frame_nowcast_flood.zip',
      name: 'people-in-pov-exposed-to-nowcast-flooding',
      text: 'People in poverty exposed to event flooding',
      title: 'People in poverty  exposed to event flooding',

      onEachFeature: (feature, layer) => {
        layer.bindPopup(`${feature.properties.village_na} - ${Math.round(feature.properties.pop_p06_ex)}`)
      },
      pointToLayer: function (feature, latlng) {

        const toColor = pop_exposed => {
          if (pop_exposed > 500) return "#bf0d0d"
          if (pop_exposed > 250) return "#f19203"
          if (pop_exposed > 50) return "#e3ea08"
          return '#30a524'
        }

        const geojsonMarkerOptions = {
          radius: Math.round(Math.log2(feature.properties.pop_p06_ex)),
          fillColor: toColor(feature.properties.pop_p06_ex),
          color: "#000",
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        };
        return window.L.circleMarker(latlng, geojsonMarkerOptions);
      },
      legend: [
        {text: '0-50', color: '#30a524'},
        {text: '50-250', color: '#e3ea08'},
        {text: '250-500', color: '#f19203'},
        {text: '> 500', color: '#bf0d0d'},
      ]
    },
    active_agriculture_exposed_to_flooding: {
      url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/nowcast/2020-05-17/village_agriculture_exposed_during_jul14_nowcast.zip',
      name: 'active-agriculture-exposed-to-nowcast-flooding',
      text: 'Active agriculture exposed to event flooding',
      title: 'Active agriculture exposed to event flooding',

      onEachFeature: (feature, layer) => {
        layer.bindPopup(`${feature.properties.village_na} - ${Math.round(feature.properties.km2)} km2`)
      },
      pointToLayer: function (feature, latlng) {

        const toColor = pop_exposed => {
          if (pop_exposed > 2000) return "#bf0d0d"
          if (pop_exposed > 1000) return "#f19203"
          if (pop_exposed > 500) return "#e3ea08"
          return '#30a524'
        }

        const geojsonMarkerOptions = {
          radius: Math.round(Math.log(feature.properties.km2) / Math.log(4)),
          fillColor: toColor(feature.properties.km2),
          color: "#000",
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        };
        return window.L.circleMarker(latlng, geojsonMarkerOptions);
      },
      legend: [
        {text: '0-50', color: '#30a524'},
        {text: '50-250', color: '#e3ea08'},
        {text: '250-500', color: '#f19203'},
        {text: '> 500', color: '#bf0d0d'},
      ]
    },
    railway_exposed_to_flooding: {
      url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/nowcast/2020-05-17/village_railway_exposed_to_nowcast_july_14_flood.zip',
      name: 'railway-exposed-to-nowcast-flooding',
      text: 'Railway exposed to event flooding',
      title: 'Railway exposed to event flooding (m)',

      onEachFeature: (feature, layer) => {
        layer.bindPopup(`${feature.properties.village_na} - ${Math.round(feature.properties.sum)}m`)
      },
      pointToLayer: function (feature, latlng) {

        const geojsonMarkerOptions = {
          radius: 5,
          fillColor: '#E3EA08',
          color: "#000",
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        };
        return window.L.circleMarker(latlng, geojsonMarkerOptions);
      },
      legend: [
        {text: 'Railway track exposed', color: '#e3ea08'},
        {text: 'Both railway track and station exposed', color: '#bf0d0d'},
      ]
    },

};

export default LayerConfig

const GloFasForecast = {
  url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/glofas/{d}.tif',
  name: 'glofast-forecast',
  title: 'GloFast Forecast',
  pixelValuesToColorFn: (([value]) => {
    if (value === 0) return `rgba(0,0,0,0)`;
    if (value < 500) return '#deebf7';
    if (value < 1000) return '#9ecae1';
    return '#3182bd'
  }),
  maxZoom: 10
}

const ExtentForecast = {
  url: 'https://tiff-library.s3.eu-west-2.amazonaws.com/india/flood_maps/flood_extent_percentile_frames.zip',
  name: 'forecast-extents',
  title: 'Flood forecast extent',
  style: (feature) => {
    const confidence = feature.properties.percentile;
    let color = confidence === 90 ? '#FF0000' : (confidence === 50 ? '#FFFF00' : '#00FF00')
    return {
      stroke: true,
      weight: 2,
      color: color,
      opacity: 0.4,
      fillOpacity: 0.4,
    }
  },
}









const MapPageConfig = [
  backgroundConfig,
  physicalGeographyConfig,
  socialGeographyConfig,
  floodHazardConfig,
  floodFlowConfig,
  floodRiskConfig,

  // currentPolicyConfig,
]

export {
  HazardTypes as FloodTypes,
  HazardOptions,
  Epochs,
  ClimateFutures,
  GloFasForecast,
  ExtentForecast,
  MapPageConfig,
  floodHazards
};

